import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import api from '~/services/api';

import { useAuth } from './auth';

type StudentProps = {
  id: number;
  name: string;
  email: string;
  password: string;
  cellphone: string;
  accepted_terms: boolean;
  address_country: string;
  address_state: string;
  address_city: string;
  address_neighborhood: string;
  address_street: string;
  address_zip_code: string;
  address_number: string;
  address_complement: string;
  image_url: string;
  role: string;
  created_at: string;
  updated_at: string;
  student: {
    status: string;
    id: number;
    user_id: number;
    professional_id: number;
    gym_id: string;
    cpf: string;
    genre: string;
    observations: string;
    birth_date: string;
    created_at: string;
    updated_at: string;
  };
};

type StudentData = {
  studentData: StudentProps;
  updateStudent: (studentId: string | number) => void;
  refreshStudentData: () => Promise<void>;
  setStudent: (student: StudentProps) => void;
};

export const StudentContext = createContext({} as StudentData);

type StudentProviderProps = {
  children: ReactNode;
};

export function StudentProvider({ children }: StudentProviderProps) {
  const { user } = useAuth();

  const fakeData: StudentProps = {
    id: 0,
    name: '',
    email: '',
    password: '',
    cellphone: '',
    accepted_terms: false,
    address_country: '',
    address_state: '',
    address_city: '',
    address_neighborhood: '',
    address_street: '',
    address_zip_code: '',
    address_number: '',
    address_complement: '',
    image_url: '',
    role: '',
    created_at: new Date().toString(),
    updated_at: new Date().toString(),
    student: {
      status: '',
      observations: '',
      id: 0,
      user_id: 0,
      professional_id: 0,
      gym_id: '',
      cpf: '',
      genre: '',
      birth_date: new Date().toString(),
      created_at: new Date().toString(),
      updated_at: new Date().toString(),
    },
  };

  const [studentData, setStudentData] = useState(fakeData);
  const [studentId, setStudentId] = useState<string | number>();

  const loadStudent = useCallback(async () => {
    const { data } = await api.get(
      `professionals/${user?.professional?.id}/students/${studentId}`,
    );

    setStudentData(data);
  }, [studentId, user]);

  const refreshStudentData = useCallback(async () => {
    await loadStudent();
  }, [loadStudent]);

  useEffect(() => {
    user && studentId && loadStudent();
  }, [studentId, user, loadStudent]);

  const updateStudent = useCallback((id: string | number) => {
    setStudentId(id);
  }, []);

  const setStudent = useCallback((student: StudentProps) => {
    setStudentData(student);
  }, []);

  return (
    <StudentContext.Provider
      value={{
        studentData,
        updateStudent,
        refreshStudentData,
        setStudent,
      }}
    >
      {children}
    </StudentContext.Provider>
  );
}

export const useStudent = (): StudentData => {
  const context = useContext(StudentContext);

  if (!context) {
    throw new Error('useStudent must be used within a StudentProvider!');
  }

  return context;
};
